
import { pqrsdText } from "./pqrsdLabel";// Plain-language descriptions of the entries in a PQRSD's events[] log.
//
// The log drawer used to headline every entry with its category alone, so ten
// emails read as ten identical "Notificación enviada" rows and the fields that
// said what actually happened (which email, to whom, until when) sat below as
// raw keys, codes and ISO timestamps. describeEvent builds the headline from
// the entry's own data; the helpers below render the detail lines readably.
//
// Kept free of React so it can be tested against the template keys the cloud
// functions really send (see pqrsdEventDescriptions.test.js).

// Every `template` key the PQRSD functions pass to sendCitizenOrStaffEmail.
export const EMAIL_TEMPLATE_LABEL = {
  citizen_submission: "Constancia de radicación",
  citizen_in_progress: "Su PQRSD está en trámite",
  citizen_response_interim: "Respuesta interina",
  citizen_response_final: "Respuesta final",
  citizen_sla_warning: "Aviso: el plazo de respuesta está por vencer",
  citizen_sla_breached: "Aviso: venció el plazo de respuesta",
  citizen_info_requested: "Requerimiento de información",
  citizen_info_reminder: "Recordatorio del requerimiento de información",
  citizen_info_extension: "Prórroga concedida",
  citizen_info_received: "Constancia: recibimos la información",
  citizen_desistimiento: "Notificación del desistimiento y del recurso",
  citizen_appeal_reminder: "Recordatorio: el plazo del recurso está por vencer (80 %)",
  citizen_appeal_received: "Constancia: recibimos el recurso de reposición",
  citizen_appeal_decided: "Decisión del recurso de reposición",
  citizen_desistimiento_final: "El archivo quedó en firme",
  staff_assigned: "PQRSD asignada",
  staff_reassigned: "PQRSD reasignada",
  staff_forwarded: "PQRSD reenviada",
  staff_returned: "PQRSD devuelta",
  staff_group_routed: "PQRSD enrutada a la dependencia",
  staff_breach: "Alerta: plazo de respuesta vencido",
  staff_info_expired: "Alerta: venció el plazo del ciudadano",
  staff_info_replied: "El ciudadano completó el requerimiento",
  staff_info_request_review: "Requerimiento pendiente de su aprobación",
  staff_appeal_filed: "Recurso de reposición por resolver",
};

// Origin codes carried in `trigger`.
export const TRIGGER_LABEL = {
  citizen_request: "a solicitud del ciudadano",
  citizen_reopen: "reapertura del ciudadano",
  info_request: "requerimiento de información",
  info_reply: "respuesta del ciudadano al requerimiento",
  withdrawn: "requerimiento retirado",
  reassign: "reasignación",
  interim_response: "respuesta interina",
  appeal_granted: "recurso de reposición concedido",
  appeal_denied: "recurso de reposición negado",
  appeal_window_closed: "venció el plazo del recurso sin interponerse",
};

const DATE_KEYS = new Set(["deadline", "dueAt", "lastDay", "notifiedAt", "decisionDueAt"]);

export function formatEventDate(value) {
  if (!value) return null;
  const d = value?.toDate ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
}

// A detail line's value as a person reads it: dates as dates, origin codes as
// words, email template keys as their subject.
function formatEventValueRaw(key, value) {
  if (value === undefined || value === null || value === "") return null;
  if (DATE_KEYS.has(key)) return formatEventDate(value) || String(value);
  if (key === "trigger") return TRIGGER_LABEL[value] || String(value);
  if (key === "template") return EMAIL_TEMPLATE_LABEL[value] || String(value);
  if (key === "reason" && value === "no_email") return "El ciudadano no registró correo electrónico";
  if (key === "decision") return value === "grant" ? "Reponer" : value === "deny" ? "Confirmar" : String(value);
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

const withDate = (text, value, prefix) => {
  const date = formatEventDate(value);
  return date ? `${text} ${prefix} ${date}` : text;
};

const radicado = (id) => (id ? ` (${id})` : "");

// One line that says what happened, built from the entry's own fields. Falls
// back to `fallbackLabel` (the category) for anything it does not know.
function describeEventRaw(ev, fallbackLabel) {
  const type = ev?.eventType || ev?.tipoEvento || "";
  const e = ev || {};
  switch (type) {
    case "EMAIL_SENT": {
      const what = EMAIL_TEMPLATE_LABEL[e.template] || e.subject || "Correo";
      const toCitizen = String(e.template || "").startsWith("citizen_");
      const who = toCitizen ? "al ciudadano" : e.to ? `a ${e.to}` : "";
      return `Correo ${who}: ${what}`.replace(/\s+:/, ":");
    }
    case "EMAIL_SKIPPED": {
      const what = EMAIL_TEMPLATE_LABEL[e.template] || e.subject || "Correo";
      return `Sin correo: solicitud anónima (${what})`;
    }
    case "PQRSD_ASSIGNED":
      return `Asignada a ${e.toUserNames || e.toUserName || "un funcionario"}`;
    case "PQRSD_REASSIGNED":
      return `Reasignada a ${e.toUserName || "un funcionario"}`;
    case "PQRSD_FORWARDED":
      return `Reenviada${e.fromUserName ? ` por ${e.fromUserName}` : ""} a ${e.toUserName || "otro funcionario"}`;
    case "PQRSD_RETURNED":
      return `Devuelta${e.fromUserName ? ` por ${e.fromUserName}` : ""}${e.reason ? `: ${e.reason}` : ""}`;
    case "PQRSD_RETURNED_TO_TRIAGE":
      return `Devuelta a clasificación${e.fromGroupName ? ` desde ${e.fromGroupName}` : ""}`;
    case "PQRSD_INTAKE_VENTANILLA":
      return "Radicada por ventanilla";
    case "PQRSD_AI_CLASSIFIED":
      return "Clasificada por la IA";
    case "PQRSD_AI_ROUTED":
      return `${e.manual ? "Enrutada manualmente" : "Enrutada por la IA"}${e.groupName ? ` a ${e.groupName}` : ""}`;
    case "PQRSD_AI_NEEDS_REVIEW":
      return "La IA no pudo clasificarla: requiere clasificación manual";
    case "PQRSD_CITIZEN_CLOSED":
      return e.ratingLabel
        ? pqrsdText(`El ciudadano calificó la respuesta "${e.ratingLabel}"${e.ratingScore ? ` (${e.ratingScore}/5)` : ""} y cerró la PQRSD`)
        : pqrsdText("El ciudadano confirmó la respuesta y cerró la PQRSD");
    case "PQRSD_CITIZEN_REOPENED":
      return e.reasonLabel
        ? pqrsdText(`El ciudadano reabrió la PQRSD: ${e.reasonLabel}`)
        : pqrsdText("El ciudadano pidió más información y reabrió la PQRSD");
    case "PQRSD_SLA_RESET":
      return withDate(
        `Plazo de respuesta reiniciado${e.trigger ? ` por ${TRIGGER_LABEL[e.trigger] || e.trigger}` : ""}`,
        e.deadline, "— vence el"
      );
    case "PQRSD_SLA_PAUSED":
      return "Plazo de respuesta suspendido mientras el ciudadano completa la petición";
    case "PQRSD_SLA_RESUMED":
      return withDate(
        `Plazo de respuesta reanudado${e.trigger ? ` (${TRIGGER_LABEL[e.trigger] || e.trigger})` : ""}`,
        e.deadline, "— vence el"
      );
    case "PQRSD_INFO_REQUESTED":
      return withDate(`Requerimiento de información enviado${radicado(e.requerimientoFiledId)}`, e.dueAt, "— el ciudadano tiene hasta el");
    case "PQRSD_INFO_REPLIED": {
      const count = Number(e.itemCount);
      const parts = [Number.isFinite(count) && count > 0 ? `${count} ${count === 1 ? "ítem" : "ítems"}` : null, e.late ? "fuera de plazo" : null]
        .filter(Boolean);
      return `El ciudadano respondió el requerimiento${parts.length ? ` (${parts.join(", ")})` : ""}`;
    }
    case "PQRSD_INFO_REQUEST_WITHDRAWN":
      return `Requerimiento de información retirado${e.reason ? `: ${e.reason}` : ""}`;
    case "PQRSD_INFO_EXTENSION_GRANTED":
      return withDate(
        `Prórroga concedida${e.trigger ? ` ${TRIGGER_LABEL[e.trigger] || e.trigger}` : ""}`,
        e.dueAt, "— nuevo plazo del ciudadano:"
      );
    case "PQRSD_INFO_EXPIRED":
      return withDate("Venció el plazo del ciudadano sin respuesta", e.dueAt, "— vencía el");
    case "PQRSD_DESISTIMIENTO_DECREED":
      return withDate(`Desistimiento tácito decretado${radicado(e.actoFiledId)}`, e.lastDay, "— recurso hasta el");
    case "PQRSD_APPEAL_REMINDER_SENT":
      return withDate("Recordatorio del recurso de reposición enviado al ciudadano", e.lastDay, "— vence el");
    case "PQRSD_APPEAL_FILED":
      return withDate("El ciudadano interpuso recurso de reposición", e.decisionDueAt, "— resolver a más tardar el");
    case "PQRSD_APPEAL_GRANTED":
      return `Recurso de reposición resuelto: se repone el desistimiento${radicado(e.decisionFiledId)}`;
    case "PQRSD_APPEAL_DENIED":
      return `Recurso de reposición resuelto: se confirma el desistimiento${radicado(e.decisionFiledId)}`;
    case "PQRSD_DESISTIMIENTO_FINAL":
      return e.trigger === "appeal_denied"
        ? "Desistimiento en firme: se confirmó al resolver el recurso"
        : withDate("Desistimiento en firme: venció el plazo del recurso sin interponerse", e.lastDay, "— vencía el");
    case "RESPONSE_FILED_INTERIM":
      return `Respuesta interina radicada${radicado(e.responseFiledId)}`;
    case "RESPONSE_FILED_FINAL":
      return `Respuesta final radicada${radicado(e.responseFiledId)}`;
    case "RESPONSE_FLOW_STARTED":
      return "Flujo de aprobación de la respuesta iniciado";
    case "RESPONSE_FLOW_STEP_DONE":
      return "Paso de aprobación de la respuesta completado";
    case "RESPONSE_FLOW_REJECTED":
      return `Respuesta devuelta en la aprobación${e.reason ? `: ${e.reason}` : ""}`;
    case "RESPONSE_FLOW_DONE":
      return "Flujo de aprobación de la respuesta completado";
    default:
      return fallbackLabel || type || "Evento";
  }
}

// What people read goes out with the institution's name for the module
// (Config → PQRSD → General); the maps above keep the default wording.
export const formatEventValue = (key, value) => pqrsdText(formatEventValueRaw(key, value));
export const describeEvent = (ev, fallbackLabel) => pqrsdText(describeEventRaw(ev, fallbackLabel));
